import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Bot,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Phone,
  RotateCcw,
  Filter,
  Shield,
  MapPin,
  DollarSign,
  Undo2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUS_STYLES = {
  AUTO_BOOKED: 'bg-green-100 text-green-700 border-green-300',
  MANUAL_APPROVAL: 'bg-amber-100 text-amber-700 border-amber-300',
  REJECTED: 'bg-red-100 text-red-700 border-red-300',
  REVERSED: 'bg-slate-200 text-slate-700 border-slate-300',
  ACCEPT: 'bg-green-100 text-green-700',
  COUNTER: 'bg-amber-100 text-amber-700',
  REJECT: 'bg-red-100 text-red-700',
};

export default function LoadApprovals({ user }) {
  const [tab, setTab] = useState('auto');
  // Phase 3 audit
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dispatchers, setDispatchers] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    dispatcher_id: 'all',
    date_from: '',
    date_to: '',
  });
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [whyTarget, setWhyTarget] = useState(null); // approval row currently showing AI reasoning popover

  // Phase 2 pending
  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  useEffect(() => {
    fetchAudit();
    fetchDispatchers();
  }, []);

  useEffect(() => {
    if (tab === 'pending') fetchPending();
  }, [tab]);

  const fetchDispatchers = () => {
    api
      .get('/load-approvals/dispatchers')
      .then((res) => setDispatchers(res.data.dispatchers || []))
      .catch(() => {});
  };

  const fetchAudit = () => {
    setLoadingAudit(true);
    const params = {};
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.dispatcher_id !== 'all') params.dispatcher_id = filters.dispatcher_id;
    if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
    if (filters.date_to) {
      const d = new Date(filters.date_to);
      d.setHours(23, 59, 59, 999);
      params.date_to = d.toISOString();
    }
    api
      .get('/load-approvals', { params })
      .then((res) => {
        setRows(res.data.approvals || []);
        setSummary(res.data.summary || null);
      })
      .catch(() => toast.error('Failed to load approvals'))
      .finally(() => setLoadingAudit(false));
  };

  const fetchPending = () => {
    setLoadingPending(true);
    api
      .get('/ai-dispatch/pending')
      .then((res) => setPending(res.data.loads || []))
      .catch(() => toast.error('Failed to load pending approvals'))
      .finally(() => setLoadingPending(false));
  };

  const approvePending = (id) => {
    api
      .patch(`/ai-dispatch/approval/${id}/approve`)
      .then((res) => {
        toast.success(res.data.message);
        fetchPending();
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const rejectPending = (id) => {
    api
      .patch(`/ai-dispatch/approval/${id}/reject`)
      .then((res) => {
        toast.success(res.data.message);
        fetchPending();
      })
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const submitReverse = async () => {
    if (!reverseTarget) return;
    setReversing(true);
    const approvalId = reverseTarget.id;
    try {
      await api.post(`/load-approvals/${approvalId}/reverse`, {
        reason: reverseReason || 'Reversed by broker',
      });
      setReverseTarget(null);
      setReverseReason('');
      fetchAudit();
      // 5-second undo toast — click Undo to restore the auto-book
      toast.success('Load booking reversed', {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await api.post(`/load-approvals/${approvalId}/undo-reverse`);
              toast.success('Reverse undone — load restored to AUTO_BOOKED');
              fetchAudit();
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Undo failed');
            }
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reverse failed');
    } finally {
      setReversing(false);
    }
  };

  const clearFilters = () => {
    const cleared = { status: 'all', dispatcher_id: 'all', date_from: '', date_to: '' };
    setFilters(cleared);
    // Re-fetch directly with the cleared state, bypassing stale closure
    setLoadingAudit(true);
    api
      .get('/load-approvals')
      .then((res) => {
        setRows(res.data.approvals || []);
        setSummary(res.data.summary || null);
      })
      .catch(() => toast.error('Failed to load approvals'))
      .finally(() => setLoadingAudit(false));
  };

  const canReverse = user?.role === 'super_admin' || user?.role === 'broker' || user?.role === 'broker_manager';

  const fmtDate = (v) => {
    if (!v) return '—';
    try {
      return new Date(v).toLocaleString();
    } catch {
      return v;
    }
  };

  const stats = useMemo(
    () =>
      summary || {
        total: 0,
        auto_booked: 0,
        manual: 0,
        rejected: 0,
        reversed: 0,
      },
    [summary]
  );

  return (
    <div className="space-y-6" data-testid="load-approvals-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            Load Approvals
          </h1>
          <p className="text-slate-600 mt-1">
            Audit AI auto-booked loads and review pending recommendations from agents.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200" data-testid="tabs">
        <button
          data-testid="tab-auto"
          onClick={() => setTab('auto')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'auto'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Bot className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          AI Auto-Booked (Phase 3)
        </button>
        <button
          data-testid="tab-pending"
          onClick={() => setTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'pending'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Pending Agent Approvals (Phase 2)
        </button>
      </div>

      {/* ---- Auto-Booked Audit ---- */}
      {tab === 'auto' && (
        <>
          {/* Summary tiles */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="approvals-summary">
              <StatTile label="Total" value={stats.total} color="slate" />
              <StatTile label="Auto-Booked" value={stats.auto_booked} color="green" />
              <StatTile label="Manual Review" value={stats.manual} color="amber" />
              <StatTile label="Rejected" value={stats.rejected} color="red" />
              <StatTile label="Reversed" value={stats.reversed} color="blue" />
            </div>
          )}

          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Filters</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                  >
                    <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="AUTO_BOOKED">Auto-Booked</SelectItem>
                      <SelectItem value="MANUAL_APPROVAL">Manual Review</SelectItem>
                      <SelectItem value="REJECTED">Rejected</SelectItem>
                      <SelectItem value="REVERSED">Reversed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Dispatcher</Label>
                  <Select
                    value={filters.dispatcher_id}
                    onValueChange={(v) => setFilters((f) => ({ ...f, dispatcher_id: v }))}
                  >
                    <SelectTrigger data-testid="filter-dispatcher"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Dispatchers</SelectItem>
                      {dispatchers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name || 'Unnamed'}{d.email ? ` · ${d.email}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From</Label>
                  <Input
                    type="date"
                    data-testid="filter-from"
                    value={filters.date_from}
                    onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input
                    type="date"
                    data-testid="filter-to"
                    value={filters.date_to}
                    onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={fetchAudit} data-testid="apply-filters-btn" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Apply
                  </Button>
                  <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                    Clear
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {loadingAudit ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : rows.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bot className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-900">No approvals match your filters</p>
                <p className="text-sm text-slate-500">
                  Once Phase 3 AI evaluates a load, it will appear here for audit.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => (
                <Card
                  key={r.id}
                  data-testid={`approval-${r.id}`}
                  className={`border-l-4 ${
                    r.status === 'AUTO_BOOKED'
                      ? 'border-l-green-500'
                      : r.status === 'REVERSED'
                      ? 'border-l-slate-400'
                      : r.status === 'REJECTED'
                      ? 'border-l-red-400'
                      : 'border-l-amber-400'
                  }`}
                >
                  <CardContent className="py-4">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1 space-y-2 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`px-2.5 py-0.5 rounded-md text-[11px] font-bold border ${
                              STATUS_STYLES[r.status] || 'bg-slate-100'
                            }`}
                          >
                            {r.status?.replace('_', ' ')}
                          </span>
                          {r.leg && (
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                              {r.leg}
                            </span>
                          )}
                          <span className="text-xs text-slate-500">{fmtDate(r.created_at)}</span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                          <Info label="Broker" value={r.loadData?.brokerName || '—'} />
                          <Info
                            label="Route"
                            value={`${r.loadData?.pickupCity || '?'}, ${r.loadData?.pickupState || ''} → ${r.loadData?.dropoffCity || '?'}, ${r.loadData?.dropoffState || ''}`}
                            icon={<MapPin className="w-3 h-3 text-slate-400" />}
                          />
                          <Info
                            label="Rate"
                            value={r.loadData?.offerRate ? `$${r.loadData.offerRate}` : '—'}
                            icon={<DollarSign className="w-3 h-3 text-green-500" />}
                            valueClass="text-green-700 font-semibold"
                          />
                          <Info
                            label="Miles"
                            value={r.loadData?.miles ?? '—'}
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                          <Info label="Dispatcher" value={r.dispatcher_name || r.dispatcherId?.slice(0, 8) || '—'} />
                          <Info label="Driver" value={r.driver_name || '—'} />
                          <Info
                            label="AI Decision"
                            value={r.aiDecision?.decision || '—'}
                            valueClass="font-semibold text-slate-800"
                          />
                        </div>

                        {r.aiDecision?.reason && (
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-slate-500 italic flex-1 line-clamp-1">AI: {r.aiDecision.reason}</p>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[10px] text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              onClick={() => setWhyTarget(r)}
                              data-testid={`why-btn-${r.id}`}
                            >
                              <Bot className="w-3 h-3 mr-1" />
                              Why?
                            </Button>
                          </div>
                        )}

                        {r.reversed_at && (
                          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                            <Undo2 className="w-3 h-3 inline mr-1 text-slate-500" />
                            <span className="font-semibold">Reversed</span> {fmtDate(r.reversed_at)}
                            {r.reversed_by_name ? ` by ${r.reversed_by_name}` : ''} ·{' '}
                            <span className="italic">{r.reverse_reason}</span>
                          </div>
                        )}
                      </div>

                      {/* Action */}
                      <div className="flex lg:flex-col gap-2 shrink-0">
                        {canReverse && r.status === 'AUTO_BOOKED' && !r.reversed_at && (
                          <Button
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`reverse-${r.id}`}
                            onClick={() => {
                              setReverseTarget(r);
                              setReverseReason('');
                            }}
                          >
                            <RotateCcw className="w-4 h-4 mr-1.5" /> Reverse
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ---- Pending Agent Approvals ---- */}
      {tab === 'pending' && (
        <>
          {loadingPending ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : pending.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-lg font-medium text-slate-900">No Pending Approvals</p>
                <p className="text-sm text-slate-500">
                  When agents use the AI Dispatch Agent, their recommendations will appear here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pending.map((item) => (
                <Card key={item.id} className="border-l-4 border-l-purple-400" data-testid={`pending-${item.id}`}>
                  <CardContent className="py-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            className={`px-3 py-1 rounded-lg text-xs font-bold ${
                              STATUS_STYLES[item.ai_decision?.decision] || 'bg-slate-100'
                            }`}
                          >
                            AI: {item.ai_decision?.decision}
                          </span>
                          <span className="text-sm font-bold text-purple-600">
                            Score: {item.ai_decision?.score}/100
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="text-slate-500">Broker:</span>{' '}
                            <strong>{item.load_data?.brokerName || 'N/A'}</strong>
                          </div>
                          <div>
                            <span className="text-slate-500">Route:</span>{' '}
                            <strong>
                              {item.load_data?.pickupCity} → {item.load_data?.dropoffCity}
                            </strong>
                          </div>
                          <div>
                            <span className="text-slate-500">Rate:</span>{' '}
                            <strong className="text-green-600">${item.load_data?.offerRate}</strong>
                          </div>
                          <div>
                            <span className="text-slate-500">Miles:</span>{' '}
                            <strong>{item.load_data?.miles}</strong>
                          </div>
                        </div>
                        {item.ai_decision?.brokerScript && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center gap-1 mb-1">
                              <Phone className="w-3 h-3 text-green-600" />
                              <span className="text-xs font-bold text-green-700">Broker Script:</span>
                            </div>
                            <p className="text-xs text-green-800 italic">
                              "{item.ai_decision.brokerScript}"
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex lg:flex-col gap-2 shrink-0">
                        {canReverse && (
                          <>
                            <Button
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => approvePending(item.id)}
                              data-testid={`approve-${item.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" /> Approve
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => rejectPending(item.id)}
                              data-testid={`reject-${item.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Reverse dialog */}
      <Dialog open={!!reverseTarget} onOpenChange={(o) => !o && setReverseTarget(null)}>
        <DialogContent data-testid="reverse-dialog">
          <DialogHeader>
            <DialogTitle>Reverse AI Auto-Booking</DialogTitle>
            <DialogDescription>
              This will mark the booking as reversed and release the load back to available.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm bg-slate-50 rounded p-3 border">
              <p><strong>Broker:</strong> {reverseTarget?.loadData?.brokerName}</p>
              <p><strong>Route:</strong> {reverseTarget?.loadData?.pickupCity} → {reverseTarget?.loadData?.dropoffCity}</p>
              <p><strong>Rate:</strong> ${reverseTarget?.loadData?.offerRate}</p>
            </div>
            <div>
              <Label htmlFor="reverse-reason">Reason (optional)</Label>
              <Input
                id="reverse-reason"
                data-testid="reverse-reason-input"
                placeholder="e.g., Wrong equipment, better lane found, cancelled by broker"
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={submitReverse}
              disabled={reversing}
              data-testid="confirm-reverse-btn"
            >
              {reversing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
              Confirm Reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Reasoning Peek — shows every input the AI used to make its decision */}
      <Dialog open={!!whyTarget} onOpenChange={(o) => !o && setWhyTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="ai-reasoning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-600" />
              Why did the AI book this?
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Full reasoning breakdown from Phase 3 autonomous dispatch.
            </DialogDescription>
          </DialogHeader>
          {whyTarget && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Score</p>
                  <p className="text-2xl font-bold text-emerald-800">{whyTarget.aiDecision?.score ?? '—'}<span className="text-xs text-slate-500 font-normal">/100</span></p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <p className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Decision</p>
                  <p className="text-lg font-bold text-blue-800">{whyTarget.aiDecision?.decision || '—'}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Rate/Mile</p>
                  <p className="text-base font-semibold">${Number(whyTarget.aiDecision?.ratePerMile || 0).toFixed(2)}/mi</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Deadhead</p>
                  <p className="text-base font-semibold">{Number(whyTarget.aiDecision?.deadheadMiles || 0)} mi</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fuel Est.</p>
                  <p className="text-base font-semibold">${Number(whyTarget.aiDecision?.fuelEstimate || 0)}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Profit</p>
                  <p className="text-base font-semibold text-emerald-800">${Number(whyTarget.aiDecision?.profitAfterFuel || 0).toLocaleString()}</p>
                </div>
              </div>
              {whyTarget.aiDecision?.reason && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Reasoning</p>
                  <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 italic">{whyTarget.aiDecision.reason}</p>
                </div>
              )}
              {whyTarget.aiDecision?.brokerScript && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Broker Script</p>
                  <p className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded p-2">"{whyTarget.aiDecision.brokerScript}"</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setWhyTarget(null)} data-testid="close-why-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, color }) {
  const colors = {
    slate: 'bg-slate-50 border-slate-200 text-slate-900',
    green: 'bg-green-50 border-green-200 text-green-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color] || colors.slate}`}>
      <p className="text-[10px] uppercase tracking-widest opacity-70 font-semibold">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

function Info({ label, value, icon, valueClass = '' }) {
  return (
    <div className="min-w-0">
      <span className="text-slate-500 text-xs">{label}:</span>{' '}
      <span className={`text-slate-800 ${valueClass}`}>
        {icon && <span className="inline-block mr-1 align-middle">{icon}</span>}
        {value}
      </span>
    </div>
  );
}
